import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Body1, Body2, InteractiveElement, StyledIconButton, borderRadius, styled, fontSizes } from "@iventis/styles";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { RepeatedTimeRangeAsDate } from "@iventis/types";
import { dateToTimeInputString, useIsElementScrollableOnRender } from "@iventis/utilities";
import { Divider } from "@mui/material";
import React, { useState } from "react";
import { utcToZonedTime } from "date-fns-tz";

interface AttributeRepeatedTimeRangeProps {
    attributeName: string;
    values: RepeatedTimeRangeAsDate[];
    onAddTimeRange: () => void;
    canUserEdit: boolean;
}

export const AttributeRepeatedTimeRangeComponent = ({ attributeName, values, onAddTimeRange, canUserEdit }: AttributeRepeatedTimeRangeProps) => {
    const [collapsed, setCollapsed] = useState<boolean>(true);
    const translate = useIventisTranslate();

    const { elementScrollable, onElementRender } = useIsElementScrollableOnRender();

    const displayText =
        values == null || values.length === 0
            ? translate(Content.map8.dates.objectDetails.noRangesAvailable)
            : values.length === 1
            ? translate(Content.map8.dates.objectDetails.rangeAvailable)
            : translate(Content.map8.dates.objectDetails.rangesAvailable, { rangesAmount: values.length });

    return (
        <Input collapsed={collapsed} $isListOfTimeRangesScrollable={elementScrollable}>
            <div className="display-text">
                {displayText}
                <StyledIconButton
                    className="expand-button"
                    onClick={() => setCollapsed(!collapsed)}
                    size="small"
                    data-testid={`expand-repeated-time-range-values-${attributeName}`}
                >
                    <FontAwesomeIcon icon={["fas", collapsed ? "caret-down" : "caret-up"]} />
                </StyledIconButton>
            </div>
            {!collapsed && (
                <>
                    <TimeRangeDivider $isListOfTimeRangesScrollable={elementScrollable} />
                    <div className="time-range-items" ref={onElementRender}>
                        {values.length === 0 ? (
                            <>
                                <NoAttributeRepeatedTimeRangeItem />
                                {canUserEdit && (
                                    <AddRepeatedTimeRanges onClick={onAddTimeRange}>
                                        <Body1>{translate(Content.map8.dates.objectDetails.add)}</Body1>
                                    </AddRepeatedTimeRanges>
                                )}
                            </>
                        ) : (
                            <>
                                {values.map((value) => (
                                    <AttributeRepeatedTimeRangeItem key={value.id} item={value} />
                                ))}
                            </>
                        )}
                    </div>
                </>
            )}
        </Input>
    );
};

const AttributeRepeatedTimeRangeItem = ({ item }: { item: RepeatedTimeRangeAsDate }) => (
    <AttributeRepeatedTimeRangeItemContainer>
        <Body2 className="repeat-time-range">{item.name}</Body2>
        <div className="time-ranges">
            <div className="time-row">
                <AttributeRepeatedDate testId={`start-date-${item.name}`} date={item.startDate} />
                -
                <AttributeRepeatedDate testId={`end-date-${item.name}`} date={item.endDate} />
            </div>
            <div className="time-row">
                <AttributeRepeatedTime testId={`start-time-${item.name}`} time={item.startTime} />
                -
                <AttributeRepeatedTime testId={`end-time-${item.name}`} time={item.endTime} />
            </div>
        </div>
    </AttributeRepeatedTimeRangeItemContainer>
);

const NoAttributeRepeatedTimeRangeItem = () => {
    const translate = useIventisTranslate();

    return (
        <AttributeRepeatedTimeRangeItemContainer>
            <Body2 className="repeat-time-range repeated-time-range-placeholder-title">{translate(Content.map8.dates.objectDetails.noDataAvailable)}</Body2>
            <div className="time-ranges repeated-time-range-placeholder">
                <div className="time-row">
                    <StyledBody1>dd/mm/yyyy</StyledBody1>-<StyledBody1>dd/mm/yyyy</StyledBody1>
                </div>
                <div className="time-row">
                    <StyledBody1>hh:mm</StyledBody1>-<StyledBody1>hh:mm</StyledBody1>
                </div>
            </div>
        </AttributeRepeatedTimeRangeItemContainer>
    );
};

const AttributeRepeatedDate = ({ date, testId }: { date: Date; testId: string }) => <Body1 data-testid={testId}>{utcToZonedTime(date, "UTC").toLocaleDateString("en-GB")}</Body1>;

const AttributeRepeatedTime = ({ time, testId }: { time: Date; testId: string }) => <Body1 data-testid={testId}>{dateToTimeInputString(time)}</Body1>;

const AttributeRepeatedTimeRangeItemContainer = styled.div`
    .repeat-time-range {
        font-weight: 500;
    }

    .time-ranges {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        & .time-row {
            display: flex;
            gap: 4px;
        }
    }
`;

const StyledBody1 = styled(Body1)`
    font-size: ${fontSizes.small};
`;

const Input = styled.div<{ collapsed: boolean; $isListOfTimeRangesScrollable: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    color: ${(props) => props.theme.typographyColors.core};

    box-sizing: border-box;
    border: 1px solid ${(props) => props.theme.shades.three};
    width: 100%;
    height: ${({ collapsed }) => (collapsed ? "44px" : "auto")};
    border-radius: ${borderRadius.standard};
    padding: ${(props) => (props.$isListOfTimeRangesScrollable ? "0px 0px 0px 12px" : "0px 12px")};
    background-color: ${(props) => props.theme.typographyColors.blank};

    .display-text {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    .expand-button {
        margin-right: ${(props) => (props.$isListOfTimeRangesScrollable ? "12px" : "0px")};
    }

    .time-range-items {
        max-height: 250px;
        padding: ${(props) => (props.$isListOfTimeRangesScrollable ? "10px 10px 10px 0px" : "10px 0px")};
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .repeated-time-range-placeholder-title {
        color: ${(props) => props.theme.shades.two};
    }

    .repeated-time-range-placeholder {
        color: ${(props) => props.theme.shades.three};
    }
`;

const AddRepeatedTimeRanges = styled(InteractiveElement)`
    width: fit-content;
    p {
        color: ${(props) => props.theme.primaryColors.focus};
        font-weight: 500;
        text-decoration: underline;
    }
`;

const TimeRangeDivider = styled(Divider)<{ $isListOfTimeRangesScrollable: boolean }>`
    width: ${(props) => (props.$isListOfTimeRangesScrollable ? "calc(100% - 12px)" : "100%")};
`;
